/**
 * Screen 1i — the 3D property model and its slicer.
 *
 * The framework is stratigraphic: the volume is built between two well
 * markers, and layer k is the same geological horizon in every well rather than
 * the same depth. On a structured field the two differ widely, and only the
 * first can be read.
 *
 * There are two slices on this screen because one proves nothing on its own:
 * the horizontal slice shows how a property is distributed across one horizon,
 * and the vertical slice shows how it changes from layer to layer. Both are
 * taken from the same volume.
 */
import { useMemo, useState } from "react";
import { api, unpage, type Page } from "../api";
import {
  Button,
  Checkbox,
  Chip,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  NumberField,
  Panel,
  ParamCaption,
  Segmented,
  Slider,
  Toggle,
} from "../components/ui";
import { useAsync, useMapView, useRampsVersion, useStored } from "../hooks";
import { ScreeningTable } from "../components/DataScope";
import { count } from "../lib/plural";
import { unitsFor } from "../lib/units";
import { ColorBar, ColorBarEditor, ScaleBarSvg } from "../components/ColorBar";
import {
  defaultScale,
  makePainter,
  scaleBar,
  type ColorScale,
} from "../lib/colorscale";
import {
  meetMapping,
  seenBox,
  viewBoxFor,
} from "../lib/basemap";
import { coordinateStep, coordinateTicks } from "../lib/ticks";
import MapZoomControls from "../components/MapZoomControls";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import type { GeoModel, Project, Screening, Well } from "../types";
import { DEFAULT_CONTROLS, Volume3D, VolumePanels, type VolumeControls } from "./Volume3D";

/** Lebar ruang gambar peta horizon. Tingginya mengikuti bentuk lapangan. */
const MAP_W = 1000;

/**
 * Ruang gambar penampang tegak.
 *
 * Tingginya angka tetap dan bukan turunan bentuk selang, tidak seperti pada
 * peta horizon. Selang setebal seratus meter di atas lintasan sepuluh
 * kilometer yang digambar setara skala tinggal setipis satu garis, dan yang
 * dicari orang pada penampang justru perubahan dari lapisan ke lapisan.
 */
const SECTION_W = 1000;
const SECTION_H = 320;

/**
 * Sumbu koordinat survei di atas peta horizon.
 *
 * Angkanya adalah easting dan northing sungguhan, bukan nomor inline dan
 * crossline. Peta ini memang selalu menempatkan sumurnya memakai koordinat —
 * `grid.x0`..`x1` dan `y0`..`y1` datang dari kepala sumur dalam CRS proyek —
 * jadi yang belum ada hanyalah angkanya di tepi. Nomor baris dan kolom kisi
 * sengaja tidak dipakai di sini: yang dicari orang pada peta horizon adalah
 * tempat, dan "kolom 24" bukan tempat mana pun di luar model ini. Ia bahkan
 * berpindah arti begitu modelnya dibangun ulang dengan cacah sel yang lain.
 *
 * Sumbunya mengikuti pandangan dan bukan seluruh kisi. Sumbu yang menandai
 * seluruh model akan membawa setiap angkanya ke luar layar pada geseran
 * pertama, dan sumbu yang tidak terlihat sama saja dengan tidak ada.
 */
function CoordinateAxes({
  grid,
  width,
  height,
  seen,
  crs,
}: {
  grid: GeoModel["grid"];
  width: number;
  height: number;
  seen: ReturnType<typeof seenBox>;
  crs: string;
}) {
  const spanX = grid.x1 - grid.x0;
  const spanY = grid.y1 - grid.y0;
  if (!(spanX > 0) || !(spanY > 0)) return null;

  const { left, top, width: seenW, height: seenH, k } = seen;

  const toX = (east: number) => ((east - grid.x0) / spanX) * width;
  const toY = (north: number) => height - ((north - grid.y0) / spanY) * height;
  const eastAt = (x: number) => grid.x0 + (x / width) * spanX;
  const northAt = (y: number) => grid.y0 + ((height - y) / height) * spanY;

  const west = eastAt(left);
  const east = eastAt(left + seenW);
  const south = northAt(top + seenH);
  const north = northAt(top);

  // Satu langkah untuk kedua sumbu, diambil dari sisi yang lebih pendek. Kisi
  // yang persegi adalah cara peta ini mengatakan bahwa ia setara skala; dua
  // langkah yang berbeda akan menghapus keterangan itu justru pada peta yang
  // baru saja diperbaiki supaya pantas mengatakannya.
  const step = coordinateStep(Math.min(east - west, north - south), 3);
  const eastings = coordinateTicks(west, east, step);
  const northings = coordinateTicks(south, north, step);

  const halo = {
    paintOrder: "stroke" as const,
    stroke: "var(--label-halo)",
    strokeWidth: 3 * k,
  };
  const tickFont = `600 ${(10 * k).toFixed(2)}px var(--font-mono)`;

  // Angka di tepi digambar ke arah dalam. Garis kisi paling timur jatuh persis
  // di tepi kanan, dan angka yang dipusatkan padanya kehilangan separuh
  // dirinya di luar peta — "51800" yang terbaca sebagai koordinat yang salah,
  // bukan sebagai angka yang terpotong.
  const anchorFor = (x: number): "start" | "middle" | "end" =>
    x < left + 24 * k ? "start" : x > left + seenW - 24 * k ? "end" : "middle";

  return (
    <g pointerEvents="none">
      {eastings.map((value) => (
        <line key={`ev${value}`} x1={toX(value)} x2={toX(value)} y1={top} y2={top + seenH}
              stroke="var(--grid-line)" strokeWidth={k} />
      ))}
      {northings.map((value) => (
        <line key={`nv${value}`} x1={left} x2={left + seenW} y1={toY(value)} y2={toY(value)}
              stroke="var(--grid-line)" strokeWidth={k} />
      ))}
      {eastings.map((value) => (
        <text key={`et${value}`} x={toX(value)} y={top + seenH - 6 * k}
              textAnchor={anchorFor(toX(value))}
              style={{ font: tickFont, fill: "var(--label-ink)", ...halo }}>
          {value.toFixed(0)}
        </text>
      ))}
      {northings.map((value) =>
        // Baris paling bawah sudah milik angka easting. Sebuah northing yang
        // jatuh di sana tercetak menimpanya, dan dua bilangan tujuh angka yang
        // saling menembus tidak terbaca sebagai keduanya melainkan sebagai
        // kerusakan. Yang dilepas hanya angkanya; garis kisinya tetap ada.
        toY(value) > top + seenH - 16 * k ? null : (
          // Angka northing duduk di atas garisnya, dan garis paling utara bisa
          // jatuh di baris pertama peta — jadi angkanya ditahan supaya tidak
          // naik ke luar bingkai.
          <text key={`nt${value}`} x={left + 6 * k}
                y={Math.max(toY(value) - 4 * k, top + 10 * k)}
                style={{ font: tickFont, fill: "var(--label-ink)", ...halo }}>
            {value.toFixed(0)}
          </text>
        ),
      )}
      {/* Satuan dan CRS-nya, sebab sederet tujuh angka tanpa keduanya bisa
          dibaca sebagai apa saja. Duduknya di atas baris easting, bukan di
          sudut atas: sudut itu milik kendali zoom. */}
      <text x={left + seenW - 6 * k} y={top + seenH - 20 * k} textAnchor="end"
            style={{ font: `400 ${(9 * k).toFixed(2)}px var(--font-mono)`, fill: "var(--ink-4)", ...halo }}>
        m · {crs}
      </text>
    </g>
  );
}

/** One layer as a map, with the wells overlaid at their true positions. */
function SliceMap({
  model,
  property,
  layer,
  showWells,
  showCoords,
  crs,
  onPickRow,
  row,
  painter,
  barMetres,
}: {
  model: GeoModel;
  property: string;
  layer: number;
  showWells: boolean;
  showCoords: boolean;
  crs: string;
  onPickRow: (row: number) => void;
  row: number;
  painter: ReturnType<typeof makePainter>;
  barMetres: number | null;
}) {
  const grid = model.grid;
  const values = model.properties[property]?.[layer] ?? [];
  const spanX = grid.x1 - grid.x0;
  const spanY = grid.y1 - grid.y0;

  // Ruang gambar mengikuti bentuk lapangan, bukan sebaliknya. Kotak 1000 × 1000
  // yang dipakai sebelumnya menjejalkan rentang timur-barat dan utara-selatan
  // ke sisi yang sama panjang, sehingga lapangan selebar 10,6 km dan setinggi
  // 4,0 km tergambar sebagai persegi: satu kilometer ke utara menempati 2,7
  // kali lebih banyak piksel daripada satu kilometer ke timur. Peta seperti itu
  // masih bisa dibaca sebagai pola, tetapi tidak bisa diukur — dan begitu
  // tepinya bertanda koordinat, ia berbohong dengan angka.
  const mapH = spanX > 0 && spanY > 0 ? (MAP_W * spanY) / spanX : MAP_W;
  const bar = scaleBar(spanX, MAP_W, barMetres);
  const cw = MAP_W / grid.nx;
  const ch = mapH / grid.ny;

  const px = (x: number) => ((x - grid.x0) / spanX) * MAP_W;
  // The map Y axis runs north, the screen Y axis runs down — flipped here.
  const py = (y: number) => mapH - ((y - grid.y0) / spanY) * mapH;

  // Peta model digambar di ruang koordinatnya sendiri, jadi zoom-nya menggeser
  // `viewBox` dan bukan menghitung ulang ribuan persegi.
  const zoom = useMapView({ space: (rect) => meetMapping(rect.width, rect.height, MAP_W, mapH) });

  /** Piksel elemen → satuan kisi, lewat pita kosong "meet" dan pandangan. */
  const gridUnit = (clientY: number, box: DOMRect) => {
    const m = meetMapping(box.width, box.height, MAP_W, mapH);
    return ((clientY - box.top - m.offsetY) / m.scale - zoom.view.y) / zoom.view.zoom;
  };

  // Semua yang tergambar di atas peta memakai satu kotak tampak yang sama, dan
  // dilawan-skalakan dengan `k` yang sama. Simbol sumur yang membesar empat
  // kali bersama zoom menutupi justru bagian peta yang sedang diperiksa
  // orang — dan pada zoom itu batang skalanya sudah lama tergeser ke luar
  // layar, hilang tepat ketika jarak paling ingin diukur.
  const seen = seenBox(zoom.view, MAP_W, mapH);
  const k = seen.k;

  return (
    <div style={{ position: "relative" }}>
    <MapZoomControls
      zoom={zoom.view.zoom}
      home={zoom.home}
      onIn={() => zoom.zoomBy(1.4)}
      onOut={() => zoom.zoomBy(1 / 1.4)}
      onReset={zoom.reset}
      corner="top-right"
    />
    <svg
      ref={zoom.bind}
      viewBox={viewBoxFor(zoom.view, MAP_W, mapH)}
      preserveAspectRatio="xMidYMid meet"
      style={{
        display: "block",
        width: "100%",
        // Height-capped so the horizontal and vertical slices both fit on
        // one screen: making the user scroll between them
        // defeats the point of showing two slices at once.
        maxHeight: 470,
        background: "var(--canvas)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        touchAction: "none",
        cursor: zoom.panning ? "grabbing" : undefined,
      }}
      onClick={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        const fy = gridUnit(event.clientY, box) / mapH;
        onPickRow(Math.max(0, Math.min(grid.ny - 1, Math.round((1 - fy) * (grid.ny - 1)))));
      }}
    >
      {values.map((line, j) =>
        line.map((v, i) =>
          v === null ? null : (
            <rect
              key={`${i}-${j}`}
              x={i * cw}
              y={mapH - (j + 1) * ch}
              width={cw + 0.5}
              height={ch + 0.5}
              fill={painter.at(v)}
            />
          ),
        ),
      )}
      {showCoords && (
        <CoordinateAxes grid={grid} width={MAP_W} height={mapH} seen={seen} crs={crs} />
      )}
      <line
        x1={seen.left}
        x2={seen.left + seen.width}
        y1={mapH - (row + 0.5) * ch}
        y2={mapH - (row + 0.5) * ch}
        stroke="var(--accent)"
        strokeWidth={2 * k}
        strokeDasharray={`${8 * k} ${6 * k}`}
      />
      <ScaleBarSvg pixels={bar.pixels} label={bar.label} k={k}
                   x={seen.left + 24 * k} y={seen.top + seen.height - 32 * k} />
      {showWells &&
        model.wells.map((w) => (
          <g key={w.id}>
            <circle cx={px(w.x)} cy={py(w.y)} r={6 * k} fill="none" stroke="var(--label-halo)" strokeWidth={3 * k} />
            <circle cx={px(w.x)} cy={py(w.y)} r={6 * k} fill="none" stroke="var(--label-ink)" strokeWidth={1.5 * k} />
            <text
              x={px(w.x) + 9 * k}
              y={py(w.y) + 5 * k}
              style={{ font: `600 ${(13 * k).toFixed(2)}px var(--font-mono)`, fill: "var(--label-ink)", paintOrder: "stroke", stroke: "var(--label-halo)", strokeWidth: 3.5 * k }}
            >
              {w.name}
            </text>
          </g>
        ))}
    </svg>
    </div>
  );
}

/**
 * Nilai awal kedua sakelar kisi, diwarisi dari sakelar tunggal sebelumnya.
 *
 * Sampai sakelarnya dibelah dua, kisi koordinat peta dan penampang diatur
 * bersama lewat kunci `evolog.geo.coords`. Yang sudah mematikannya tidak
 * boleh mendapatkannya menyala kembali hanya karena satu sakelar menjadi dua:
 * setelan yang hilang tanpa sepatah kata pun terbaca sebagai setelan yang
 * tidak pernah benar-benar tersimpan. Dibaca sekali di awal, dan hanya
 * terpakai selama kunci barunya belum ada.
 */
function inheritedCoords(): boolean {
  try {
    return localStorage.getItem("evolog.geo.coords") !== "false";
  } catch {
    // Penjelajahan pribadi: tidak ada yang bisa dibaca, dan menyala adalah
    // bawaan yang sama dengan yang didapat pemakai baru.
    return true;
  }
}

/**
 * Apa yang sebenarnya diukur ke bawah pada penampang tegak.
 *
 * Sumbu mendatarnya koordinat, sumbu tegaknya bukan. Pada kotak kedalaman,
 * lapisan k memang sebuah kedalaman dan boleh ditulis dalam meter. Di antara
 * dua marker ia bukan: lapisan k adalah horizon geologi yang sama di setiap
 * sumur, dan horizon itu duduk lebih dalam di satu sumur daripada di sumur
 * sebelahnya — sebuah garis mendatar di penampang ini bukan kedalaman tetap.
 * Menuliskan meter di sana berarti mengarang satu angka dari dua yang berbeda,
 * jadi yang tertulis adalah seberapa jauh ke bawah selang itu.
 */
interface DepthAxis {
  /** Nilai pada puncak selang dan pada dasarnya, dalam satuan tampilannya. */
  min: number;
  max: number;
  format: (value: number) => string;
}

/**
 * Sumbu penampang tegak: easting yang melintang, selang yang menurun.
 *
 * Langkah kedua sumbunya dihitung sendiri-sendiri, tidak seperti pada peta
 * horizon. Penampang memang dilebih-lebihkan tegaknya — seratus meter selang
 * di atas sepuluh kilometer lintasan tidak akan terlihat kalau tidak — dan
 * kisi persegi di sini justru akan mengaku setara skala padahal tidak.
 */
function SectionAxes({
  grid,
  width,
  seen,
  depth,
  nz,
  cellH,
}: {
  grid: GeoModel["grid"];
  width: number;
  seen: ReturnType<typeof seenBox>;
  depth: DepthAxis;
  nz: number;
  cellH: number;
}) {
  const spanX = grid.x1 - grid.x0;
  if (!(spanX > 0)) return null;

  const { left, top, width: seenW, height: seenH, k } = seen;

  const toX = (east: number) => ((east - grid.x0) / spanX) * width;
  const eastAt = (x: number) => grid.x0 + (x / width) * spanX;

  // Pecahan selang dipetakan lewat pusat pita lapisan, sama seperti yang
  // dipakai keterangan kedalaman di sebelah kanan: pecahan 0 adalah tengah
  // lapisan teratas, bukan tepi atas kotak. Dua hitungan yang berbeda di sini
  // menaruh angka setengah lapisan meleset dari pita yang ditunjuknya.
  const rungs = Math.max(1, nz - 1);
  const toY = (fraction: number) => (fraction * rungs + 0.5) * cellH;
  const fractionAt = (y: number) => (y / cellH - 0.5) / rungs;

  const west = eastAt(left);
  const east = eastAt(left + seenW);
  const eastStep = coordinateStep(east - west, 6);
  const eastings = coordinateTicks(west, east, eastStep);

  const span = depth.max - depth.min;
  const valueAt = (y: number) => depth.min + fractionAt(y) * span;
  const shallow = valueAt(top);
  const deep = valueAt(top + seenH);
  const depthStep = coordinateStep(Math.abs(deep - shallow), 4);
  const depths = coordinateTicks(Math.min(shallow, deep), Math.max(shallow, deep), depthStep);

  const halo = {
    paintOrder: "stroke" as const,
    stroke: "var(--label-halo)",
    strokeWidth: 3 * k,
  };
  const tickFont = `600 ${(10 * k).toFixed(2)}px var(--font-mono)`;
  const anchorFor = (x: number): "start" | "middle" | "end" =>
    x < left + 24 * k ? "start" : x > left + seenW - 24 * k ? "end" : "middle";

  return (
    <g pointerEvents="none">
      {eastings.map((value) => (
        <line key={`ev${value}`} x1={toX(value)} x2={toX(value)} y1={top} y2={top + seenH}
              stroke="var(--grid-line)" strokeWidth={k} />
      ))}
      {depths.map((value) => (
        <line key={`dv${value}`} x1={left} x2={left + seenW}
              y1={toY((value - depth.min) / span)} y2={toY((value - depth.min) / span)}
              stroke="var(--grid-line)" strokeWidth={k} />
      ))}
      {eastings.map((value) =>
        // Sudut kanan bawah penampang milik kendali zoom, dan angka yang
        // tercetak di belakangnya tidak terbaca sebagai angka. Garis kisinya
        // tetap digambar — yang dilepas hanya bilangannya, dan tetangganya
        // sebelah kiri masih menyebut langkahnya.
        toX(value) > left + seenW - 46 * k ? null : (
          <text key={`et${value}`} x={toX(value)} y={top + seenH - 6 * k}
                textAnchor={anchorFor(toX(value))}
                style={{ font: tickFont, fill: "var(--label-ink)", ...halo }}>
            {value.toFixed(0)}
          </text>
        ),
      )}
      {depths.map((value) => {
        const y = toY((value - depth.min) / span);
        // Pita bawah sudah milik batang skala, nama marker dasar, dan angka
        // easting — ketiganya menumpuk di tepi kiri yang sama dengan angka
        // selang ini. Tepi kiri atas milik nama marker puncak. Angka yang
        // jatuh di keduanya tercetak menimpa dan tidak terbaca sebagai
        // keduanya; garis kisinya tetap digambar.
        if (y > top + seenH - 62 * k || y < top + 20 * k) return null;
        return (
          <text key={`dt${value}`} x={left + 6 * k} y={y - 4 * k}
                style={{ font: tickFont, fill: "var(--label-ink)", ...halo }}>
            {depth.format(value)}
          </text>
        );
      })}
    </g>
  );
}

/** The vertical slice: one grid row, every layer, top to bottom. */
function SliceSection({
  model,
  property,
  row,
  layer,
  painter,
  barMetres,
  onLayer,
  depthLabel,
  showCoords,
  depthAxis,
}: {
  model: GeoModel;
  property: string;
  row: number;
  layer: number;
  painter: ReturnType<typeof makePainter>;
  barMetres: number | null;
  onLayer: (layer: number) => void;
  depthLabel: (layer: number) => string;
  showCoords: boolean;
  depthAxis: DepthAxis;
}) {
  const grid = model.grid;
  const bar = scaleBar(grid.x1 - grid.x0, SECTION_W, barMetres);
  const cw = SECTION_W / grid.nx;
  const ch = SECTION_H / grid.nz;
  const [dragging, setDragging] = useState(false);

  // Menyeret di irisan ini sudah berarti memindahkan garis horizon, jadi
  // geseran pandangan mundur ke Shift atau tombol tengah. Rodanya tetap
  // memperbesar seperti di peta.
  const zoom = useMapView({
    space: (rect) => meetMapping(rect.width, rect.height, SECTION_W, SECTION_H),
    panWhen: (event) => event.button === 1 || (event.button === 0 && event.shiftKey),
  });
  const seen = seenBox(zoom.view, SECTION_W, SECTION_H);
  const k = seen.k;

  /** Which layer the pointer is over, in the SVG's own coordinates. */
  const layerAt = (event: React.PointerEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return layer;
    const box = svg.getBoundingClientRect();
    const m = meetMapping(box.width, box.height, SECTION_W, SECTION_H);
    const y = ((event.clientY - box.top - m.offsetY) / m.scale - zoom.view.y) / zoom.view.zoom;
    return Math.max(0, Math.min(grid.nz - 1, Math.floor(y / ch)));
  };

  return (
    <div style={{ position: "relative" }}>
    <MapZoomControls
      zoom={zoom.view.zoom}
      home={zoom.home}
      onIn={() => zoom.zoomBy(1.4)}
      onOut={() => zoom.zoomBy(1 / 1.4)}
      onReset={zoom.reset}
      corner="bottom-right"
    />
    <svg
      ref={zoom.bind}
      viewBox={viewBoxFor(zoom.view, SECTION_W, SECTION_H)}
      style={{
        display: "block",
        width: "100%",
        background: "var(--canvas)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        touchAction: "none",
      }}
    >
      {Array.from({ length: grid.nz }, (_, k) =>
        (model.properties[property]?.[k]?.[row] ?? []).map((v, i) =>
          v === null ? null : (
            <rect
              key={`${k}-${i}`}
              x={i * cw}
              y={k * ch}
              width={cw + 0.5}
              height={ch + 0.5}
              fill={painter.at(v)}
            />
          ),
        ),
      )}
      {showCoords && (
        <SectionAxes grid={grid} width={SECTION_W} seen={seen}
                     depth={depthAxis} nz={grid.nz} cellH={ch} />
      )}
      {/* The horizon line, dragged on the section itself. Reaching for a
          slider in the side rail to move a line you are looking at breaks the
          loop between question and answer; here the line is the control. */}
      <rect
        x={0}
        y={0}
        width={SECTION_W}
        height={SECTION_H}
        fill="transparent"
        style={{ cursor: "ns-resize" }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
          onLayer(layerAt(event));
        }}
        onPointerMove={(event) => dragging && onLayer(layerAt(event))}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
      />
      <rect x={0} y={layer * ch} width={SECTION_W} height={ch} fill="none"
            stroke="var(--accent)" strokeWidth={2 * k} pointerEvents="none" />
      {/* Keterangan lapisan menempel pada tepi kanan pandangan supaya tidak
          ikut tergeser ke luar layar, tetapi tingginya tetap milik lapisan
          yang ditunjuknya. */}
      <g pointerEvents="none">
        <rect x={seen.left + seen.width - 168 * k} y={layer * ch + ch / 2 - 9 * k}
              width={162 * k} height={18 * k}
              rx={3 * k} fill="var(--label-halo)" opacity={0.82} />
        <text x={seen.left + seen.width - 160 * k} y={layer * ch + ch / 2 + 4 * k}
              style={{ font: `600 ${(11 * k).toFixed(2)}px var(--font-mono)`, fill: "var(--label-ink)" }}>
          {depthLabel(layer)}
        </text>
      </g>
      {/* Tiga baris di tepi bawah penampang, dari atas ke bawah: batang skala,
          nama marker dasar, lalu angka easting. Ketiganya dulu berdesakan di
          satu baris karena hanya dua yang ada. */}
      <ScaleBarSvg pixels={bar.pixels} label={bar.label} k={k}
                   x={seen.left + 24 * k} y={seen.top + seen.height - 44 * k} />
      {/* Nama marker tetap di puncak dan dasar selangnya, bukan di tepi
          pandangan: keduanya menamai lapisan pertama dan terakhir, dan yang
          menempel di tepi layar akan mengaku bahwa selang berakhir di tempat
          pandangan kebetulan terpotong. */}
      <text x={seen.left + 6 * k} y={14 * k}
            style={{ font: `600 ${(11 * k).toFixed(2)}px var(--font-mono)`, fill: "var(--label-ink)", paintOrder: "stroke", stroke: "var(--label-halo)", strokeWidth: 3 * k }}>
        {model.top_marker}
      </text>
      {/* Duduknya di atas baris easting dan bukan menempel di dasar kotak:
          baris itu sudah milik angka koordinat, dan dua tulisan yang saling
          menembus di sana tidak terbaca sebagai keduanya. */}
      <text x={seen.left + 6 * k} y={SECTION_H - 22 * k}
            style={{ font: `600 ${(11 * k).toFixed(2)}px var(--font-mono)`, fill: "var(--label-ink)", paintOrder: "stroke", stroke: "var(--label-halo)", strokeWidth: 3 * k }}>
        {model.base_marker}
      </text>
    </svg>
    </div>
  );
}

/**
 * Isi rel kanan layar ini, sebagai daftar tab yang sama bentuknya dengan rel
 * layar log — satu nama, sebab rel ini satu kolom panel dan bukan empat tab.
 */
const RAIL_TABS: RailTabItem<"model">[] = [{ key: "model", label: "Model" }];

export default function GeoModelScreen({ project }: { project: Project }) {
  const markers = useAsync(
    (signal) =>
      api.get<{ markers: { name: string; wells: number }[] }>(
        `/api/projects/${project.id}/marker-names/`,
        {},
        signal,
      ),
    [project.id],
  );

  const options = markers.data?.markers ?? [];
  const [top, setTop] = useState("");
  const [base, setBase] = useState("");
  const [nz, setNz] = useState(16);
  // Depth box when a project has no markers. Framing on depth is a weaker
  // assumption than framing on horizons, so the choice is visible rather than
  // inferred, and the payload repeats which one was used.
  // Lebar rel kanan. Panel di sini memuat penyunting skala warna dan kendali
  // volume tiga dimensi — keduanya kendali yang dipegang lama sambil memandang
  // hasilnya, bukan sekali setel lalu ditinggal, jadi seberapa besar porsi
  // layar yang pantas untuknya berbeda dari satu pekerjaan ke pekerjaan lain.
  const [railWidth, setRailWidth] = useStored("evolog.geo.rail", 288);
  // Kerangka dan propertinya dipilih sebelum model dibangun; sesudah itu yang
  // dikerjakan adalah memutar volumenya dan menggeser irisannya, dan tidak
  // satu pun di antaranya menuntut daftar marker terlihat. Disimpan, dengan
  // alasan yang sama seperti lebarnya sendiri.
  const [railCollapsed, setRailCollapsed] = useStored("evolog.geo.rail.collapsed", false);
  const [framework, setFramework] = useState<"markers" | "depth">("markers");
  const [depthTop, setDepthTop] = useState(450);
  const [depthBase, setDepthBase] = useState(650);
  /**
   * Cara menyebarkan tiap lapisan ke grid, dan angka yang menyertainya.
   *
   * Tiga cara, dan tidak satu pun berpura-pura kriging: jarak terbalik yang
   * lazim dipakai tangan, nilai sumur terdekat yang jelek dipandang dan justru
   * karena itu jujur, dan rerata Gauss yang paling halus. Yang dipilih ikut
   * tercetak di kepala layar sesudah model dibangun — dua model dengan angka
   * yang berbeda tidak bisa dibandingkan sampai diketahui apakah yang berbeda
   * adalah datanya atau cara menyebarkannya.
   *
   * Disimpan, sebab ini setelan cara kerja: orang yang memilih Gauss untuk
   * sebuah lapangan memilihnya untuk lapangan itu, bukan untuk satu bangunan.
   */
  const [method, setMethod] = useStored<"idw" | "nearest" | "gaussian">(
    "evolog.geo.method", "idw");
  const [power, setPower] = useStored("evolog.geo.power", 2);
  const [radius, setRadius] = useStored("evolog.geo.radius", 1000);
  /**
   * Interval lateral, dalam meter — bukan dalam banyaknya sel.
   *
   * Yang dipikirkan orang adalah "seratus meter"; berapa sel yang dibutuhkan
   * untuk itu bergantung pada luas lapangannya, dan luas itu diketahui server,
   * bukan layar ini. Nol berarti "biar server yang memilih", yaitu 48 x 48
   * sel sebagaimana sebelumnya.
   */
  const [cell, setCell] = useStored("evolog.geo.cell", 0);
  const [property, setProperty] = useState("PHIE");

  /**
   * Jaring radius. Di layar inilah ia paling berarti: `geomodel.build` menerima
   * `well_ids`, jadi yang terjaring bukan sekadar yang tersorot melainkan
   * satu-satunya sumur yang menyuapi interpolasinya. Modelnya benar-benar
   * berubah, dan panelnya boleh mengatakan itu tanpa mengurangi apa pun.
   *
   * Namanya "Filter radius", bukan "Radius" — layar ini sudah punya jangkauan
   * Gauss yang juga sebuah jarak dalam meter, dan dua kotak bernama sama di
   * satu rel adalah dua angka yang akan tertukar.
   */
  const [centerWell, setCenterWell] = useStored<number | null>(
    "evolog.geo.radius.center", null);
  const [screenKm, setScreenKm] = useStored("evolog.geo.radius.km", 5);
  const [screenMode, setScreenMode] = useStored<"surface" | "trajectory">(
    "evolog.geo.radius.mode", "trajectory");
  const wellList = useAsync(
    (signal) =>
      api.get<Page<Well>>("/api/wells/", { project: project.id }, signal)
        .then((page) => unpage(page, signal)),
    [project.id],
  );
  const screening = useAsync(
    (signal) =>
      api.get<Screening>(
        `/api/projects/${project.id}/screening/`,
        {
          center_well: centerWell,
          radius: Math.round(screenKm * 1000),
          radius_mode: screenMode,
        },
        signal,
      ),
    [project.id, centerWell, screenKm, screenMode],
    { skip: centerWell === null },
  );
  const terjaring = useMemo(
    () => (screening.data?.wells ?? [])
      .filter((row) => row.state !== "outside radius" && row.state !== "no coordinates")
      .map((row) => row.well_id),
    [screening.data],
  );
  // Which curves to model. Not every project carries the same ones — NPRA has
  // RHOB in all fourteen wells and PHIE in nine.
  const [properties, setProperties] = useState<string[]>(["PHIE", "VSH"]);
  const [layer, setLayer] = useState(0);
  const [row, setRow] = useState(0);

  const [showWells, setShowWells] = useState(true);
  // Kisi koordinat disimpan, tidak seperti penanda sumur di atasnya: siapa
  // yang bekerja dengan koordinat bekerja dengannya sepanjang hari, dan
  // menyalakannya kembali pada setiap kunjungan adalah pajak harian.
  //
  // Satu sakelar untuk kedua gambar, dan keduanya memang menjawab pertanyaan
  // yang sama. Tetapi angka yang menolong di peta bisa merepotkan di
  // penampang — penampang itu seperempat tinggi peta dan tepi bawahnya sudah
  // dihuni tiga hal — jadi masing-masing punya sakelarnya sendiri.
  const coordsWere = useMemo(inheritedCoords, []);
  const [mapCoords, setMapCoords] = useStored("evolog.geo.coords.map", coordsWere);
  const [sectionCoords, setSectionCoords] = useStored("evolog.geo.coords.section", coordsWere);
  // One scale per curve: RHOB and PHIE want different ramps and different
  // ends, and switching between them should not reset what was set up.
  const [scales, setScales] = useState<Record<string, ColorScale>>({});
  // null means "pick a round number that fits"; a fixed length is honoured as
  // given, because someone comparing two sheets wants the same bar on both.
  const [barMetres, setBarMetres] = useState<number | null>(null);
  const [model, setModel] = useState<GeoModel | null>(null);
  /** What layer k means, in the units the framework can actually support. */
  const units = unitsFor(project.unit_system);

  const depthLabel = (k: number) => {
    if (!model) return `layer ${k + 1}`;
    const f = model.grid.nz > 1 ? k / (model.grid.nz - 1) : 0;
    if (model.framework === "depth") {
      const top = parseFloat(model.top_marker);
      const base = parseFloat(model.base_marker);
      if (Number.isFinite(top) && Number.isFinite(base)) {
        const md = top + f * (base - top);
        return `${units.formatDepth(md)} MD · layer ${k + 1}/${model.grid.nz}`;
      }
    }
    return `layer ${k + 1}/${model.grid.nz} · ${(f * 100).toFixed(0)}% down`;
  };

  /**
   * Sumbu tegak penampang, dalam satuan yang memang bisa dipertanggungjawabkan.
   *
   * Kotak kedalaman menamai puncak dan dasarnya dengan angka, jadi lapisan k
   * benar-benar sebuah kedalaman dan sumbunya boleh berbunyi meter. Di antara
   * dua marker tidak ada satu kedalaman pun yang berlaku untuk seluruh
   * penampang, jadi yang tertulis adalah seberapa jauh ke bawah selang itu —
   * angka yang sama dengan yang sudah dipakai keterangan lapisan.
   */
  const depthAxis = useMemo<DepthAxis>(() => {
    const top = model ? parseFloat(model.top_marker) : Number.NaN;
    const base = model ? parseFloat(model.base_marker) : Number.NaN;
    if (model?.framework === "depth" && Number.isFinite(top) && Number.isFinite(base)) {
      return {
        min: units.depth(top),
        max: units.depth(base),
        format: (value) => `${value.toFixed(0)} ${units.depthUnit}`,
      };
    }
    return { min: 0, max: 100, format: (value) => `${value.toFixed(0)}%` };
  }, [model, units]);

  /** Northing baris yang sedang dipenampangkan — penampang ini lintasan tetap. */
  const rowNorthing = useMemo(() => {
    if (!model || model.grid.ny < 2) return null;
    const { y0, y1, ny } = model.grid;
    return y0 + (row / (ny - 1)) * (y1 - y0);
  }, [model, row]);

  const [view, setView] = useState<"3d" | "2d">("3d");
  const [controls, setControls] = useState<VolumeControls>(DEFAULT_CONTROLS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nama marker tiba dalam urutan stratigrafi, paling dangkal lebih dulu, jadi
  // pilihan awalnya adalah selang terdalam yang benar-benar bisa dibangun:
  // puncak paling dangkal sampai dasar paling dalam. Marker yang hanya ada di
  // bawah tiga sumur dilewati — itu bukan permukaan, dan memilihnya sebagai
  // kerangka membuat tombol "Build model" gagal sebelum disentuh siapa pun.
  const framable = options.filter((marker) => marker.wells >= 3);
  const usable = framable.length >= 2 ? framable : options;
  const topValue = top || usable[0]?.name || "";
  const baseValue = base || usable[usable.length - 1]?.name || "";

  const build = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<GeoModel>(`/api/projects/${project.id}/geomodel/`, {
        top_marker: topValue,
        base_marker: baseValue,
        properties,
        nx: 48,
        ny: 48,
        nz,
        method,
        ...(method === "idw" ? { power } : {}),
        ...(method === "gaussian" ? { radius } : {}),
        ...(cell > 0 ? { cell } : {}),
        ...(framework === "depth" ? { depth_top: depthTop, depth_base: depthBase } : {}),
        // Bukan sorotan melainkan bahan: yang tidak terkirim di sini tidak ikut
        // menyuapi interpolasinya sama sekali.
        ...(centerWell !== null && terjaring.length ? { wells: terjaring } : {}),
      });
      setModel(result);
      setLayer(0);
      setRow(Math.floor(result.grid.ny / 2));
      setControls({
        ...DEFAULT_CONTROLS,
        i: Math.floor(result.grid.nx / 2),
        j: Math.floor(result.grid.ny / 2),
        k: Math.floor(result.grid.nz / 2),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setModel(null);
    } finally {
      setBusy(false);
    }
  };

  // `model?.stats[...]` menghentikan rantainya di `model` saja: model yang
  // datang tanpa `stats` — muatan lama, atau properti yang tidak terhitung
  // karena tidak satu sumur pun membawanya — meledak di tengah render dan
  // meninggalkan layar putih tanpa sepatah kata pun. Yang dijaga di sini
  // bukan tipenya melainkan keadaannya.
  const dataMin = model?.stats?.[property]?.min ?? 0;
  const dataMax = model?.stats?.[property]?.max ?? 1;
  const scale = scales[property] ?? defaultScale(property);
  // `versiRamp`: warna palet yang disunting di tempat ikut tergambar.
  const versiRamp = useRampsVersion();
  const painter = useMemo(
    () => makePainter(scale, dataMin, dataMax),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scale, dataMin, dataMax, versiRamp],
  );
  const setScale = (next: ColorScale) => setScales((prev) => ({ ...prev, [property]: next }));


  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <div className="scroll-y" style={{ flex: 1, minWidth: 0, padding: 14 }}>
        {!model && !busy && (
          <Empty>
            {error ??
              (framework === "depth"
                ? "Set a depth box, choose the curves, then build."
                : options.length === 0
                  ? "This project has no markers. Switch the framework to Depth box to model it anyway."
                  : "Pick two markers as the framework, then build the model.")}
          </Empty>
        )}
        {busy && <Loading label="Upscaling the logs and spreading them across the grid…" />}
        {model && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Segmented
                value={property}
                options={Object.keys(model.properties).map((name) => ({
                  value: name,
                  label: name,
                }))}
                onChange={setProperty}
              />
              <Segmented
                value={view}
                options={[
                  { value: "3d", label: "Volume 3D" },
                  { value: "2d", label: "2D slices" },
                ]}
                onChange={setView}
              />
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
                {depthLabel(layer)}
              </span>
              <span style={{ flex: 1 }} />
              <div style={{ width: 200 }}>
                <ColorBar property={property} scale={scale} dataMin={dataMin} dataMax={dataMax} compact />
              </div>
            </div>

            {view === "3d" ? (
              <Volume3D
                model={model}
                property={property}
                painter={painter}
                barMetres={barMetres}
                controls={controls}
                onControls={setControls}
              />
            ) : (
              <>
            {/* On a depth box the layer *is* a depth, so the slider says so in
                metres. Between markers there is no single depth — the same
                horizon sits deeper in one well than another — so it says how
                far down the interval it is instead of inventing a number. */}
            <Slider
              label={model.framework === "depth" ? "Depth" : "Horizon"}
              value={layer}
              min={0}
              max={model.grid.nz - 1}
              step={1}
              onChange={(value) => setLayer(Math.round(value))}
              format={(value) => depthLabel(Math.round(value))}
            />
            <Slider
              label="Vertical section (row J)"
              value={row}
              min={0}
              max={model.grid.ny - 1}
              step={1}
              onChange={(value) => setRow(Math.round(value))}
              format={(value) => `${Math.round(value) + 1}/${model.grid.ny}`}
            />

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <div>
                <div style={{ font: "600 10px var(--font-mono)", color: "var(--ink-5)", marginBottom: 4 }}>
                  HORIZON SLICE — {depthLabel(layer)}
                </div>
                <SliceMap
                  model={model}
                  property={property}
                  layer={layer}
                  showWells={showWells}
                  showCoords={mapCoords}
                  crs={project.crs}
                  row={row}
                  onPickRow={setRow}
                  painter={painter}
                  barMetres={barMetres}
                />
              </div>
              <div>
                <div style={{ font: "600 10px var(--font-mono)", color: "var(--ink-5)", marginBottom: 4 }}>
                  VERTICAL SECTION — row {row + 1}
                  {rowNorthing === null ? "" : ` · N ${rowNorthing.toFixed(0)}`}, west to east ·
                  drag the horizon line to slide it
                </div>
                <SliceSection model={model} property={property} row={row} layer={layer}
                              painter={painter} barMetres={barMetres}
                              onLayer={setLayer} depthLabel={depthLabel}
                              showCoords={sectionCoords} depthAxis={depthAxis} />
              </div>
            </div>
            </>
            )}
          </div>
        )}
      </div>

      {/* Pegangan lebarnya hanya ada selama ada yang bisa dilebarkan. */}
      {!railCollapsed && (
        <Resizer
          width={railWidth}
          onChange={setRailWidth}
          min={250}
          max={560}
          label="Resize the model panel"
        />
      )}
      {railCollapsed ? (
        <RailStrip
          tabs={RAIL_TABS}
          active="model"
          onSelect={() => setRailCollapsed(false)}
          expandLabel="Show the model panel"
        />
      ) : (
        <aside
          style={{
            width: railWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Kepala rel, yang tidak ikut bergulir — sebagaimana di layar lain:
              rel ini panjang, dan tombol lipat yang hanyut ke atas bersama panel
              Framework adalah tombol yang harus digulirkan kembali dulu. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 7px 5px 12px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Model</span>
            <RailCollapseButton
              side="right"
              collapsed={false}
              label="Hide the model panel"
              onClick={() => setRailCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{
              flex: 1,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <Panel title="Framework" meta={count(options.length, "marker")}>
              <Segmented
                value={framework}
                onChange={(value) => setFramework(value as "markers" | "depth")}
                options={[
                  { value: "markers", label: "Markers" },
                  { value: "depth", label: "Depth box" },
                ]}
              />
              {framework === "depth" && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--warn-ink)", lineHeight: 1.6 }}>
                  Layer k becomes the same depth in every well, not the same horizon.
                  That assumes flat, correlatable beds across the whole model.
                </span>
              )}
              {framework === "depth" ? (
                <>
                  {/* Diketik, bukan digeser. Batas atas dan bawah sebuah volume
                      adalah angka yang datang dari marker, dari laporan, atau
                      dari lembar log di sebelah — dan penggeser sepanjang lima
                      kilometer dengan langkah sepuluh meter tidak pernah bisa
                      berhenti tepat di 2.072. Penggesernya tetap ada di
                      bawahnya untuk mencari-cari. */}
                  <div style={{ display: "flex", gap: 8 }}>
                    <NumberField label="Top bound" unit="m MD" value={depthTop} step={1}
                                 min={0} max={12000} onChange={setDepthTop} />
                    <NumberField label="Base bound" unit="m MD" value={depthBase} step={1}
                                 min={0} max={12000} onChange={setDepthBase} />
                  </div>
                  <Slider label="Move the top bound" value={depthTop} min={0} max={5000} step={10}
                          onChange={(v) => setDepthTop(Math.round(v))}
                          format={(v) => `${Math.round(v)} m`} />
                  <Slider label="Move the base bound" value={depthBase} min={0} max={5000} step={10}
                          onChange={(v) => setDepthBase(Math.round(v))}
                          format={(v) => `${Math.round(v)} m`} />
                  {depthBase <= depthTop && (
                    <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--warn-ink)" }}>
                      The base bound must be deeper than the top bound.
                    </span>
                  )}
                </>
              ) : (
                <>
              <Dropdown
                label="Top marker"
                value={topValue}
                onChange={setTop}
                options={options.map((m) => ({ value: m.name, label: `${m.name} · ${m.wells} wells` }))}
              />
              <Dropdown
                label="Base marker"
                value={baseValue}
                onChange={setBase}
                options={options.map((m) => ({ value: m.name, label: `${m.name} · ${m.wells} wells` }))}
              />
              </>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {["PHIE", "VSH", "RHOB", "NPHI", "SW", "PERM"].map((name) => (
                  <Chip
                    key={name}
                    active={properties.includes(name)}
                    onClick={() =>
                      setProperties((prev) =>
                        prev.includes(name)
                          ? prev.filter((x) => x !== name) || prev
                          : [...prev, name],
                      )
                    }
                  >
                    {name}
                  </Chip>
                ))}
              </div>
              {/* Jaring radius, tepat di atas tombolnya: ia menentukan bahan
                  yang dipakai tombol itu, bukan sekadar apa yang tersorot. */}
              <Checkbox
                checked={centerWell !== null}
                label="Build from wells inside the radius only"
                onChange={(on) =>
                  setCenterWell(on ? (centerWell ?? wellList.data?.[0]?.id ?? null) : null)
                }
              />
              {centerWell !== null && (
                <>
                  <Dropdown
                    label="Centre well"
                    value={centerWell}
                    options={(wellList.data ?? []).map((w) => ({ value: w.id, label: w.name }))}
                    onChange={setCenterWell}
                  />
                  <NumberField
                    label="Filter radius"
                    unit="km"
                    value={screenKm}
                    min={0.01}
                    step={0.5}
                    onChange={setScreenKm}
                  />
                  <Segmented
                    value={screenMode}
                    size="sm"
                    options={[
                      { value: "surface", label: "Well head" },
                      { value: "trajectory", label: "Along the track" },
                    ]}
                    onChange={(next) => setScreenMode(next as "surface" | "trajectory")}
                  />
                  <ParamCaption>
                    What is caught is more than highlighted: these wells alone feed
                    the interpolation, so the model really changes. The bands are a
                    note — the framework still runs from marker to marker, not from
                    the bands.
                  </ParamCaption>
                  {screening.data?.note && (
                    <span style={{ font: "400 10px var(--font-sans)", color: "var(--warn-ink)" }}>
                      {screening.data.note}
                    </span>
                  )}
                  {/* Aturannya milik server dan disebut di sini sebelum
                      tombolnya ditekan, bukan sesudah — `geomodel.build`
                      menolak di bawah tiga sumur, dan mengetahuinya lewat pesan
                      galat berarti mengetahuinya satu putaran terlambat. */}
                  {screening.data && terjaring.length < 3 && (
                    <span style={{ font: "400 10px var(--font-sans)", color: "var(--warn-ink)" }}>
                      {terjaring.length} wells caught. A model needs at least three that
                      pierce both markers and carry coordinates — widen the radius
                      first.
                    </span>
                  )}
                </>
              )}
              <Button
                variant="primary"
                onClick={build}
                disabled={
                  busy
                  || (framework === "markers" ? !topValue || !baseValue : depthBase <= depthTop)
                  || (centerWell !== null && terjaring.length < 3)
                }
              >
                {busy ? "Membangun…" : "Build model"}
              </Button>
              {error && (
                <span style={{ font: "400 10px var(--font-sans)", color: "var(--warn-ink)" }}>{error}</span>
              )}
            </Panel>

            {screening.data && screening.data.wells.length > 0 && (
              <Panel
                title={`Caught — ${screening.data.kept} of ${screening.data.asked} wells`}
                meta={`${screenKm} km from ${screening.data.center_name}`}
              >
                <ScreeningTable screening={screening.data} maxRows={200} />
              </Panel>
            )}

            {/* Sesudah kerangkanya, sebelum hasilnya: ini kendali yang
                menentukan bentuk model yang akan dibangun, bukan cara
                memandang model yang sudah ada. */}
            <Panel
              title="Grid & interpolation"
              meta={model ? `${model.grid.nx}×${model.grid.ny}×${model.grid.nz}` : undefined}
            >
              <Dropdown
                label="Interpolation"
                value={method}
                onChange={(value) => setMethod(value as typeof method)}
                options={[
                  { value: "idw", label: "Inverse distance (IDW)" },
                  { value: "nearest", label: "Nearest well" },
                  { value: "gaussian", label: "Gaussian average" },
                ]}
              />
              <ParamCaption>
                {method === "idw"
                  ? "Every well pulls its surroundings; the further away, the weaker. The value at the borehole is kept."
                  : method === "nearest"
                    ? "The value of the nearest well, with no blending. The map becomes polygons — ugly to look at, and for that very reason it shows that between wells there is no data."
                    : "A Gaussian-weighted mean: the smoothest, and the only one that does not force the well value exactly at its borehole."}
              </ParamCaption>
              {method === "idw" && (
                <Slider label="Power" value={power} min={1} max={4} step={0.5}
                        onChange={setPower} format={(v) => v.toFixed(1)} />
              )}
              {method === "gaussian" && (
                <NumberField label="Range" unit="m" value={radius} step={50}
                             min={50} max={20000} onChange={setRadius} />
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <NumberField label="Lateral interval" unit="m" value={cell} step={25}
                             min={0} max={5000} onChange={setCell} />
                <NumberField label="Vertical layers" unit="layers" value={nz} step={1}
                             min={2} max={40} onChange={(value) => setNz(Math.round(value))} />
              </div>
              <ParamCaption>
                {cell > 0
                  ? "The cells are made as close to that interval as possible; the field's extent decides how many cells result, capped at 80 × 80."
                  : "A lateral interval of 0 leaves the choice to the server: 48 × 48 cells across the field."}
              </ParamCaption>
              {model && (
                <>
                  <KeyValue k="Lateral cell"
                            v={`${model.grid.dx.toFixed(0)} × ${model.grid.dy.toFixed(0)} m`} />
                  <KeyValue
                    k="Layer thickness"
                    v={
                      model.grid.layer_thickness === undefined
                        ? "—"
                        : `${model.grid.layer_thickness.toFixed(1)} m${
                            model.grid.layer_thickness_exact ? "" : " (mean)"
                          }`
                    }
                  />
                  <KeyValue k="Method" v={model.method.interpolation} mono={false} />
                </>
              )}
            </Panel>

            {model && (
              <>
                <Panel title="Colour bar" meta={property}>
                  <ColorBarEditor
                    property={property}
                    scale={scale}
                    onChange={setScale}
                    dataMin={dataMin}
                    dataMax={dataMax}
                  />
                </Panel>

                <Panel title="Distance bar">
                  <Segmented
                    value={barMetres === null ? "auto" : String(barMetres)}
                    onChange={(value) => setBarMetres(value === "auto" ? null : Number(value))}
                    options={[
                      { value: "auto", label: "Auto" },
                      { value: "1000", label: "1 km" },
                      { value: "5000", label: "5 km" },
                      { value: "10000", label: "10 km" },
                    ]}
                  />
                  <Slider
                    label="Custom length"
                    value={barMetres ?? 5000}
                    min={100}
                    max={100000}
                    step={100}
                    onChange={(value) => setBarMetres(Math.round(value / 100) * 100)}
                    format={(value) =>
                      value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`
                    }
                  />
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                    A fixed length is drawn as given. Two sheets meant to be compared
                    need the same bar on both, not whatever each one thought looked
                    best.
                  </span>
                </Panel>

                {view === "3d" ? (
                  <VolumePanels model={model} controls={controls} onControls={setControls}
                                depthLabel={depthLabel} />
                ) : (
                  <Panel title="Display">
                    <Toggle label="Well markers" checked={showWells} onChange={setShowWells} />
                    <Toggle label="Map coordinate grid" checked={mapCoords} onChange={setMapCoords} />
                    <Toggle label="Section coordinate grid" checked={sectionCoords}
                            onChange={setSectionCoords} />
                    <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                      Survey easting and northing in {project.crs}, not inline and
                      crossline numbers. The horizon map is equal-scale, so its grid is
                      square. The downward axis of the section names a position inside
                      the interval, not a depth — metres appear on the depth box only,
                      because between two markers a horizontal line is not one depth.
                    </span>
                  </Panel>
                )}

                <Panel title="Model contents" meta={count(model.wells.length, "well")}>
                  <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-4)", lineHeight: 1.7 }}>
                    grid {model.grid.nx} × {model.grid.ny} × {model.grid.nz}
                    <br />
                    cells {(model.grid.nx * model.grid.ny * model.grid.nz).toLocaleString("en-US")}
                    <br />
                    spacing {model.grid.dx.toFixed(0)} × {model.grid.dy.toFixed(0)} m
                  </span>
                  {(["PHIE", "VSH"] as const).map((name) => (
                    <span key={name} style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
                      {name}: {model.stats?.[name]?.min?.toFixed(3)} – {model.stats?.[name]?.max?.toFixed(3)} ·
                      mean {model.stats?.[name]?.mean?.toFixed(3)}
                    </span>
                  ))}
                </Panel>

                <Panel title="Wells used" meta={`${model.skipped.length} skipped`}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {model.wells.map((w) => (
                      <span key={w.id} style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-4)" }}>
                        {w.name} · {w.thickness.toFixed(0)} m
                      </span>
                    ))}
                  </div>
                  {model.skipped.length > 0 && (
                    <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                      Skipped: {model.skipped.map((s) => `${s.well} (${s.reason})`).join("; ")}.
                    </span>
                  )}
                </Panel>

                <Panel title="How the model was built">
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-5)", lineHeight: 1.7 }}>
                    Upscaling: {model.method.upscaling}.
                    <br />
                    Spreading: {model.method.interpolation}.
                    <br />
                    Layering: {model.method.layering}.
                  </span>
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--warn-ink)", lineHeight: 1.7 }}>
                    {model.method.caveat}
                  </span>
                </Panel>
              </>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
